import React, { useMemo, useState } from 'react';
import { catalogueRules, resolveRuleText } from '../data/ruleResolver';   // sections 728, 736: the picker shows what the resolver says (SS-XI); the catalogues are listed once
import { bnClassLineFor } from '../data/bnYardmasterClass';   // Record 614 (ruling 295): Protected / ROA class of a BN rule
import { agreementFamily, basisTermFor } from '../data/agreementRegistry';   // ruling 312 phase 1 (d): one place names the agreement
import { AGREEMENT_UNCONFIRMED_TEXT } from '../data/agreementStatus';   // ruling 324 (section 760)
import { activate } from '../data/a11yActivate';   // pass 2: role, tabIndex and Enter/Space from one tested helper

// Rule / basis-of-claim picker.
//
// Every option is a REAL rule from the governing agreement's own catalog - no invented
// taxonomy (this is what retires the hardcoded VIOLATION_TYPES list).
//   • Amtrak     - fully catalogued: title + effective text via resolveAmtrakRule(), which is
//                  location- and date-aware (surfaces the NY-only Letter No. 6 carve-outs).
//   • Conrail YM - catalogued from the 1978 RYA agreement (Rules 1-9). Titles and body text are
//                  transcription-verified against the source page images (typography cleaned,
//                  numbers and paragraph completeness checked). Shown as authoritative 1978 base
//                  text with a currency note: later national/IMOU modifications are not layered.
// Agreements without a catalog return null here, so the parent keeps the AI-suggest fallback.
//
// Output matches the existing matchedRules shape exactly: [{ rule: <label>, relevance: '' }].
// Selection is keyed on rule NUMBER so an AI suggestion (any label format) still shows as
// selected next to the catalog entry.

// Record 612 (ruling 204): memberLabel is the fixed sentence a member's suggested basis carries (memberBasis.js); '' for
// everyone else, and nothing is drawn.
export default function RuleBasisPicker({ agreement, location, asOfDate, matchedRules, onChange, analyzing, memberLabel }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [openNum, setOpenNum] = useState(null);
  // Ruling 312 phase 1 (d): which agreement this is comes from src\data\agreementRegistry.js, the one place it is said.
  const family = agreementFamily(agreement);
  const isConrailYm = family === 'conrail-ym';
  const isBn = family === 'bn-ym';
  const basisTerm = basisTermFor(agreement);
  const baseRules = catalogueRules(family);   // ruling 321 slice 3a: null when this installation carries no catalogue for it

  // Resolve a rule's effective title/text/status. Amtrak is location/date-aware; Conrail YM
  // returns its 1978 base entry (flagged ocr so the panel can show the caveat).
  // Task list section 728: ONE resolver (SS-XI). The picker called every base rule 'active' whatever the incident date, while
  // ruleResolver.js - what ClaimDetail shows - says 'not_yet_effective' for a date before the agreement took effect. The
  // picker now asks the resolver, so filing and review cannot disagree. Amtrak resolves through the same call.
  const resolveRule = (num) => resolveRuleText(agreement, num, location, asOfDate) || { text: '' };

  const options = useMemo(() => {
    if (!baseRules) return [];
    return Object.entries(baseRules)
      .filter(([num]) => /^\d+$/.test(num))
      .map(([num, r]) => ({ num: String(num), label: basisTerm + ' ' + num + ' — ' + r.title }))
      .sort((a, b) => Number(a.num) - Number(b.num));
  }, [baseRules, basisTerm]);

  const numOf = (r) => (String(r.rule).match(/\d+/) || [])[0] || '';
  const selectedNums = (matchedRules || []).map(numOf).filter(Boolean);
  const isSelected = (num) => selectedNums.includes(String(num));

  const toggle = (o) => {
    if (isSelected(o.num)) {
      onChange((matchedRules || []).filter(r => numOf(r) !== String(o.num)));
    } else {
      onChange([...(matchedRules || []), { rule: o.label, relevance: '' }]);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => {
      if (o.label.toLowerCase().includes(q)) return true;
      const eff = resolveRule(o.num);
      return (eff.text || '').toLowerCase().includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, search, location, asOfDate]);

  if (!baseRules) return null;

  const agreementShort = (agreement || '').replace(/\s*\(.*$/, '');
  const hasSelection = selectedNums.length > 0;

  return (
    <div className="form-group">
      <label className="form-label">Rule / basis of claim</label>

      {isConrailYm && (
        <div style={{ fontSize: 11, color: '#C9A24B', background: '#1A1508', border: '1px solid #4A3B12', borderRadius: 6, padding: '6px 10px', marginBottom: 8 }}>
          1978 base agreement. Later national / IMOU modifications are not yet layered — confirm current terms with your Local / General Chairman.
        </div>
      )}

      {/* Always visible: the selected basis */}
      {hasSelection ? (
        <div style={{ background: '#0A1628', border: '1px solid #2E6B4A', borderRadius: 6, padding: '8px 12px', marginBottom: 8 }}>
          <div style={{ fontSize: 11, color: '#40C070', marginBottom: 4 }}>&#x2713; Selected basis</div>
          {memberLabel ? <div style={{ fontSize: 11, color: '#FFD27A', marginBottom: 4 }}>{memberLabel}</div> : null}
          {(matchedRules || []).map(r => (
            <div key={r.rule} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: 13, color: '#F4F6F8', padding: '2px 0' }}>
              <span>{r.rule}{bnClassLineFor(agreement, r) && (
                <span style={{ display: 'block', fontSize: 10.5, color: '#8AB4E8', fontWeight: 400 }}>{bnClassLineFor(agreement, r)}</span>
              )}</span>
              <button type="button" onClick={() => onChange((matchedRules || []).filter(x => x.rule !== r.rule))}
                style={{ background: 'none', border: 'none', color: '#8AA4BF', cursor: 'pointer' }}>&#x2715;</button>
            </div>
          ))}
        </div>
      ) : (
        <div style={{ fontSize: 12, color: '#8AA4BF', marginBottom: 8 }}>
          No rule selected yet.{analyzing ? ' Suggesting from your description…' : ' Open the rulebook to choose the rule(s) violated.'}
        </div>
      )}

      {/* Collapsed control - open the rulebook on demand */}
      {!open ? (
        <button type="button" onClick={() => setOpen(true)}
          style={{ background: '#0D1B2A', border: '1px solid #2E4A6B', borderRadius: 6, color: '#8AB4E8', fontSize: 12, padding: '7px 12px', cursor: 'pointer' }}>
          {hasSelection ? '＋ Add or change the rule (open rulebook)' : '📖 Open the rulebook to choose the rule(s)'}
        </button>
      ) : (
        <div style={{ border: '1px solid #2E4A6B', borderRadius: 6, padding: 10 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 11, color: '#8AA4BF' }}>
              Search or pick from the {agreementShort} rulebook - select one or more.
            </span>
            <button type="button" onClick={() => setOpen(false)}
              style={{ background: 'none', border: 'none', color: '#8AB4E8', fontSize: 12, cursor: 'pointer' }}>▾ done</button>
          </div>

          <input aria-label="Search rules - e.g. overtime, seniority, rest day, call…"
            className="form-input"
            placeholder="Search rules - e.g. overtime, seniority, rest day, call…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ marginBottom: 8 }}
          />

          <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #16283D', borderRadius: 6 }}>
            {filtered.map(o => {
              const sel = isSelected(o.num);
              const eff = openNum === o.num ? resolveRule(o.num) : null;
              return (
                <div key={o.num} style={{ borderBottom: '1px solid #16283D' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px', background: sel ? '#0A1628' : 'transparent' }}>
                    <input aria-label="Include this rule" type="checkbox" checked={sel} onChange={() => toggle(o)} />
                    <span {...activate(() => toggle(o))} style={{ flex: 1, cursor: 'pointer', fontSize: 13, color: '#F4F6F8' }}>{o.label}</span>
                    <button type="button" onClick={() => setOpenNum(openNum === o.num ? null : o.num)}
                      style={{ background: 'none', border: 'none', color: '#8AA4BF', fontSize: 11, cursor: 'pointer' }}>
                      {openNum === o.num ? 'hide' : 'text'}
                    </button>
                  </div>
                  {eff && (
                    <div style={{ padding: '6px 12px 10px 32px', fontSize: 12, color: '#B8C4D0', whiteSpace: 'pre-wrap', background: '#0D1B2A' }}>
                      {eff.status !== 'active' && (
                        <div style={{ color: '#FF6B6B', fontWeight: 600, marginBottom: 4 }}>
                          &#9888; {eff.status.replace(/_/g, ' ')}{eff.territory ? ' at ' + eff.territory : ''} - should not be cited here
                        </div>
                      )}
                      {/* Record 614 (ruling 295): who the BN rule reaches - information, never a block. */}
                      {isBn && bnClassLineFor(agreement, { rule: o.label }) && (
                        <div style={{ color: '#8AB4E8', fontSize: 11, marginBottom: 4, whiteSpace: 'normal' }}>{bnClassLineFor(agreement, { rule: o.label })}</div>
                      )}
                      {/* Ruling 324 (section 760) */}
                      <div style={{ color: '#FFD27A', fontSize: 11, marginBottom: 4, whiteSpace: 'normal' }}>{AGREEMENT_UNCONFIRMED_TEXT}</div>
                      {eff.text ? (eff.text.length > 600 ? eff.text.slice(0, 600) + '…' : eff.text) : '(no text)'}
                    </div>
                  )}
                </div>
              );
            })}
            {filtered.length === 0 && (
              <div style={{ padding: 12, fontSize: 12, color: '#8AA4BF' }}>No rule matches "{search}".</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
